import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import PageArea from '../shared/PageArea';
import PageTitle from '../shared/PageTitle';
import LoadingWheel from '../shared/LoadingWheel';
import { StatusChip, documentStatusTone } from '../shared/StatusChip';
import { DocumentsClient, ProjectListItem, ProjectsClient, UnfiledDocumentItem } from '../../web-api-client';
import { authenticatedFetch, getApiBaseUrl } from '../../api/apiConfig';

const pageSize = 25;

const UnfiledPage: React.FC = () => {
  const { t } = useTranslation();
  const documentsClient = useMemo(() => new DocumentsClient(getApiBaseUrl(), authenticatedFetch), []);
  const projectsClient = useMemo(() => new ProjectsClient(getApiBaseUrl(), authenticatedFetch), []);

  const [items, setItems] = useState<UnfiledDocumentItem[]>([]);
  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selection, setSelection] = useState<Record<string, string>>({});

  const load = () => {
    setLoading(true);
    documentsClient
      .getApiDocumentsUnfiled(page, pageSize)
      .then((r) => {
        setItems(r.items ?? []);
        setTotal(r.totalCount ?? 0);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => load(), [page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    projectsClient
      .getApiProjects(1, 200, undefined, false)
      .then((r) => setProjects(r.items ?? []))
      .catch(() => setProjects([]));
  }, [projectsClient]);

  const refile = async (documentId: string) => {
    const projectId = selection[documentId];
    if (!projectId) {
      return;
    }

    await documentsClient.postApiDocumentsRefile(documentId, { projectId });
    load();
  };

  return (
    <PageArea>
      <PageTitle title={t('unfiledPage.title')} subtitle={t('unfiledPage.subtitle')} />
      {loading ? (
        <LoadingWheel />
      ) : items.length === 0 ? (
        <Typography color="text.secondary">{t('common.noData')}</Typography>
      ) : (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('unfiledPage.fileName')}</TableCell>
                <TableCell>{t('unfiledPage.type')}</TableCell>
                <TableCell>{t('unfiledPage.status')}</TableCell>
                <TableCell>{t('unfiledPage.assignTo')}</TableCell>
                <TableCell>{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((d) => (
                <TableRow key={d.documentId}>
                  <TableCell>{d.fileName}</TableCell>
                  <TableCell>{d.documentTypeCode}</TableCell>
                  <TableCell>
                    <StatusChip label={d.status ?? ''} tone={documentStatusTone(d.status)} />
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      displayEmpty
                      value={selection[d.documentId!] ?? ''}
                      onChange={(e) =>
                        setSelection((prev) => ({ ...prev, [d.documentId!]: e.target.value }))
                      }
                      sx={{ minWidth: 200 }}
                    >
                      <MenuItem value="">
                        <em>{t('common.search')}</em>
                      </MenuItem>
                      {projects.map((p) => (
                        <MenuItem key={p.id} value={p.id}>
                          {[p.projectNumber ?? p.internalReference, p.vesselName].filter(Boolean).join(' · ')}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="primary"
                      size="md"
                      disabled={!selection[d.documentId!]}
                      onClick={() => refile(d.documentId!)}
                    >
                      {t('unfiledPage.refile')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              count={Math.max(1, Math.ceil(total / pageSize))}
              page={page}
              onChange={(_, value) => setPage(value)}
            />
          </Box>
        </>
      )}
    </PageArea>
  );
};

export default UnfiledPage;
